import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import moment from 'moment/moment';
import { useGetAIResult } from 'resources/transaction/hooks';
import { useGetCompanyAdvancedFields } from 'resources/company/hooks';
import * as userResource from 'resources/user/hooks';


const useCachedTransactionSession = () => {
  const router = useRouter();
  const { aiTransactionId: aiTransactionIdQuery } = router.query;
  const [aiTransactionId, setAiTransactionId] = useState(aiTransactionIdQuery);
  const { data: aiResult, isLoading: isLoadingAiResult } = useGetAIResult(aiTransactionId);
  const { data: user } = userResource.useCurrentUser();
  const { data: companyFields, isLoading: isLoadingCompanyFields } = useGetCompanyAdvancedFields(user?.company_id, { enabled: !!user });

  const getColor = (confidence) => {
    // good: 80-100%
    if (confidence >= 0.8) return 'var(--green-400)';

    // medium: 50-80%
    if (confidence > 0.5) return 'var(--orange-500)';

    // bad: 0-50%
    return 'var(--red-500)';
  };

  const formatAIResult = (aiResult, companyFields) => {
    const formattedAIResult = {};

    /*
    Transform & Format an object like this:
    {
      "Financing Type": {
        "type": "string",
        "valueString": "Cash",
        "boundingRegions": [{ "pageNumber": 1, "polygon": [0.7167, 8.1489, 0.8656, 8.1489, 0.8656, 8.2709, 0.7167, 8.2709] }],
        "confidence": 0.525
      },
      ...
    }
    
    To the object like the one below:
    {
      "financing_type": {
        "label": "Financing Type",
        "color": "var(--green-400)",
        "type": "string",
        "valueString": "Cash",
        "boundingRegions": [{ "pageNumber": 1, "polygon": [0.7167, 8.1489, 0.8656, 8.1489, 0.8656, 8.2709, 0.7167, 8.2709] }],
        "confidence": 0.525
      },
      ...
    }
    */
    companyFields.forEach((companyField) => {
      const { label, name, type } = companyField;
      const aiResultFieldData = aiResult[label];
      if (!aiResultFieldData) return;

      const formatValue = (format) => {
        const dateTime = moment(aiResultFieldData.valueString);
        return dateTime.isValid() ? dateTime.format(format) : null;
      };

      let valueString = aiResultFieldData.valueString;

      if (type === 'date') {
        valueString = formatValue('YYYY-MM-DD');
      } else if (type === 'time') {
        valueString = formatValue('hh:mm a');
      }

      if (valueString !== null) {
        formattedAIResult[name] = {
          ...aiResultFieldData,
          valueString,
          label: label,
          color: getColor(aiResultFieldData.confidence),
        };
      }
    });

    const appendAdditionalData = () => {
      formattedAIResult.sourceDocument = aiResult.sourceDocument;
      formattedAIResult.sourceDocumentName = aiResult.sourceDocumentName;
    };

    appendAdditionalData();

    return formattedAIResult;
  };

  const activeSortedCompanyFields = useMemo(
    () => companyFields?.filter((field) => field.is_active)
      .sort((first, second) => first.name.localeCompare(second.name)),
    [companyFields],
  );

  const formattedData = useMemo(() => {
    return (aiResult && companyFields) ? formatAIResult(aiResult, companyFields) : null;
  }, [aiResult, companyFields]);

  const foundFields = useMemo(() => {
    return (activeSortedCompanyFields && formattedData) ? activeSortedCompanyFields.filter((field) => !!formattedData[field.name]) : [];
  }, [activeSortedCompanyFields, formattedData]);

  const storeSession = (id) => {
    window.localStorage.setItem('listedkit:aiTransactionId', id);
  };

  const deleteSession = () => {
    window.localStorage.removeItem('listedkit:aiTransactionId');
  };

  useEffect(() => {
    if (!aiTransactionIdQuery && !aiTransactionId) {
      const aiId = window.localStorage.getItem('listedkit:aiTransactionId');
      if (aiId) {
        setAiTransactionId(aiId);
      }
    }
  }, [aiTransactionIdQuery, aiTransactionId]);

  return {
    id: aiTransactionId,
    storeSession,
    deleteSession,
    formattedAiResult: formattedData,
    rawAiResult: aiResult,
    foundFields,
    isLoading: isLoadingAiResult || isLoadingCompanyFields,
  };
};

export default useCachedTransactionSession;
